import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import type { Theme } from '@material-ui/core/styles';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import TrushIcon from '../../../assets/trush.svg';

import { useStore } from '../../../contexts/StoreContext';
import { colors } from '../../const/Styles';
import { ReactSVG } from 'react-svg';

const useStyles = makeStyles((theme: Theme) => createStyles({
    TitlePaper: {
        height: 58,
        width: 664,
        borderRadius: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: `${colors.footerIconUnSelected}`,
        margin: 'auto',
        color: theme.palette.text.secondary,
        position: 'relative',
    },
    select: {
        position: 'relative',
        borderRadius: 0,
        display: 'flex',
        width: 340,
        height: 30,
        backgroundColor: `${colors.white}`,
    },
    courseName: {
        color: `${colors.white}`,
        marginRight: 17,
    },
    icon: {
        color: `${colors.footerIconSelected}`,
        top: -20,
        right: -10,
        fontSize: '4.2rem',
        position: 'absolute',
    },
}));

const Icon = styled.i`
    display: flex;
    color: ${colors.footerIconSelected};
    cursor: pointer;
    margin-left: 16px;
    svg {
        height: 32px;
    }
`;

type Props = {
    lessonId: number | undefined;
    onChange: (id: string, isNew: boolean) => void;
};

type LessonOption = {
    id: number | 0;
    label: string;
    isNew: boolean;
};

const CurriculumNameSelector: React.FC<Props> = ({ lessonId, onChange }) => {
    const classes = useStyles();
    const { tempoStore } = useStore();
    
    // 新規登録オプションを定義
    const newRegistrationOption: LessonOption = {
        id: 0,
        label: '新規登録',
        isNew: true,
    };

    // 通常のレッスンオプションを生成
    const lessonOptions: LessonOption[] = tempoStore.allLessons.map((lesson) => ({
        id: lesson.id,
        label: lesson.name,
        isNew: false,
    }));

    // 全オプション（新規登録を含む）
    const allOptions: LessonOption[] = [...lessonOptions, newRegistrationOption];

    // カスタムフィルタリング：新規登録は検索対象外で常に先頭に表示
    const filterOptions = useCallback((options: LessonOption[], { inputValue }: { inputValue: string }) => {
        // 新規登録を除外してフィルタリング
        const filtered = options.filter((option) => {
            if (option.isNew) {
                return false; // 新規登録はフィルタリング対象外
            }
            return option.label.toLowerCase().includes(inputValue.toLowerCase());
        });
        // 新規登録を末尾に追加
        return [...filtered, newRegistrationOption];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeLesson = useCallback(
        // eslint-disable-next-line @typescript-eslint/ban-types
        (_: React.ChangeEvent<{}>, value: LessonOption | null) => {
            if (!value) {
                return;
            }
            const selectedId = String(value.id);
            onChange(selectedId, value.isNew);
        },
        [onChange]
    );

    const handleClickIcon = useCallback(async () => {
        if (typeof lessonId === 'undefined') {
            return;
        }

        if (!confirm('カリキュラムを削除します。よろしいですか？')) {
            return;
        }

        try {
            await tempoStore.deleteLesson(lessonId)
            onChange('0', true);
            alert('カリキュラムを削除しました')
        } catch (error: unknown) {
            alert('カリキュラムの削除に失敗しました。\n再度時間をおいて実行してください。');
            console.error(error);
            throw error;
        }
    }, [lessonId, onChange, tempoStore])

    // 現在選択されている値を取得
    const currentValue = (() => {
        const currentId = typeof lessonId === 'number' ? lessonId : 0;
        return allOptions.find((opt) => opt.id === currentId) ?? newRegistrationOption;
    })();

    return (
        <Paper className={classes.TitlePaper}>
            <p className={classes.courseName}>レッスン名</p>
            <Autocomplete
                className={classes.select}
                options={allOptions}
                getOptionLabel={(option) => option.label}
                filterOptions={filterOptions}
                value={currentValue}
                onChange={handleChangeLesson}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant='standard'
                        placeholder='レッスン名を検索'
                    />
                )}
            />
            {typeof lessonId === 'number' && (
                <Icon onClick={handleClickIcon}>
                    <ReactSVG src={TrushIcon ?? ''} wrapper='span' />
                </Icon>
            )}
        </Paper>
    );
}

export default CurriculumNameSelector;
